import { ICoreRoutes, IResolvedRoute, IResolvedRouteAction, ITypeRouteAction } from './type-route.models.js';
import { Injectable } from '@angular/core';
import { TypeRoutesModuleConfig } from './type-routes-module.config.js';
import { IAppRoutes } from 'src/app/routes/app-routes.js';

interface IResolvedActionByUniqueId {
	[uniqueId: string]: IResolvedRouteAction | undefined;
}

interface IResolvedRouteByUniqueId {
	[uniqueId: string]: IResolvedRoute | undefined;
}

@Injectable({
	providedIn: 'root'
})
export class TypeRouteService {
	private cachedRoutes: IResolvedRoute[] | undefined;
	private cachedActionsByUniqueId: IResolvedActionByUniqueId | undefined;
	private cachedRoutesByUniqueId: IResolvedRouteByUniqueId | undefined;

	public readonly appRoutes: IAppRoutes;

	constructor(typeRoutesModuleConfig: TypeRoutesModuleConfig) {
		this.appRoutes = typeRoutesModuleConfig.appRoutes;
	}

	private getCoreRoutes(): ICoreRoutes[] {
		const coreRoutes: ICoreRoutes[] = [];

		for (const coreRouteProperty of Object.keys(this.appRoutes)) {
			const coreRoute: ICoreRoutes | undefined = this.appRoutes[coreRouteProperty];

			if (coreRoute) {
				coreRoutes.push(coreRoute);
			}
		}

		return coreRoutes;
	}

	getResolvedActions(): IResolvedActionByUniqueId {
		if (this.cachedActionsByUniqueId) {
			return this.cachedActionsByUniqueId;
		}

		this.registerRoutes();

		return this.cachedActionsByUniqueId ?? {};
	}

	getResolvedRoutes(): IResolvedRouteByUniqueId {
		if (this.cachedRoutesByUniqueId) {
			return this.cachedRoutesByUniqueId;
		}

		this.registerRoutes();

		return this.cachedRoutesByUniqueId ?? {};
	}

	registerRoutes(): IResolvedRoute[] {
		if (this.cachedRoutes) {
			return this.cachedRoutes;
		}

		this.cachedActionsByUniqueId = {};
		this.cachedRoutesByUniqueId = {};

		const resolvedRoutes: IResolvedRoute[] = [];
		const coreRoutes: ICoreRoutes[] = this.getCoreRoutes();

		for (const coreRoute of coreRoutes) {
			for (const pathProperty of Object.keys(coreRoute)) {
				const typeRoute = coreRoute[pathProperty];
				const resolvedRouteActions: IResolvedRouteAction[] = [];

				if (this.cachedRoutesByUniqueId[typeRoute.uniqueId]) {
					throw Error(`There is already a type with unique id '${typeRoute.uniqueId}'`);
				}

				for (const actionProperty of Object.keys(typeRoute.children)) {
					const routeAction = typeRoute.children[actionProperty] as ITypeRouteAction<any>;

					const resolvedRouteAction: IResolvedRouteAction = {
						...routeAction,
						mainPath: typeRoute.mainPath,
						typePath: pathProperty,
						actionPath: actionProperty
					};

					if (this.cachedActionsByUniqueId[resolvedRouteAction.uniqueId]) {
						throw Error(`There is already an action with unique id '${resolvedRouteAction.uniqueId}'`);
					}

					this.cachedActionsByUniqueId[resolvedRouteAction.uniqueId] = resolvedRouteAction;
					resolvedRouteActions.push(resolvedRouteAction);
				}

				const resolvedRoute: IResolvedRoute = {
					uniqueId: typeRoute.uniqueId,
					loadSharedData: typeRoute.loadSharedData,
					mainPath: typeRoute.mainPath,
					menuItems: typeRoute.menuItems,
					parentRoutes: typeRoute.parentRoutes,
					children: resolvedRouteActions,
					type: pathProperty,
					sharedDataHash: typeRoute.sharedDataHash
				};

				this.cachedRoutesByUniqueId[typeRoute.uniqueId] = resolvedRoute;

				resolvedRoutes.push(resolvedRoute);
			}
		}

		this.cachedRoutes = resolvedRoutes;

		return resolvedRoutes;
	}
}
